/**
 * Shared, presentation-agnostic core of the question authoring form, extracted
 * from CreateQuestionModal so the Question Bank create/edit panels (NA-197/198)
 * can reuse the same fields + validation without any modal/panel chrome.
 *
 * `useQuestionDraft` owns the draft block + isPublic + error state and the
 * validate() prologue (blockToQuestionDraft → questionDraftSchema). `QuestionForm`
 * renders the editable fields: the type's editor in its `questionModal` variant,
 * plus the "Add to public question bank" checkbox and the validation error box.
 *
 * It is seed-agnostic and knows nothing about create vs edit: callers supply the
 * initial block (fresh for create, dto-derived for edit) and own the shell,
 * header, footer, and submit mutation.
 */
import { useCallback, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { QuestionDraft } from '@course-authoring/shared-types';
import { deriveQuestionPurpose, questionDraftSchema, useTheme } from '@course-authoring/shared-types';

import { LikertScaleEditor } from '../LikertScaleEditor';
import { SingleChoiceAnswerEditor } from '../SingleChoiceAnswerEditor';
import { WorkplaceColorSpectrumEditor } from '../WorkplaceColorSpectrumEditor';
import { CategorizationEditor } from '../CategorizationEditor';
import { FreeformTextEditor } from '../FreeformTextEditor';
import { blockToQuestionDraft, type QuestionBlock } from './questionBlockConversion';
import { toScopeAccountId, useClientContext } from '../../hooks/useClientContext';

export interface UseQuestionDraftOptions {
  initialBlock: QuestionBlock;
  initialIsPublic: boolean;
  /**
   * NA-403/NA-406: when true, scope creation/edit to the active Content
   * Context — hide/force-false isPublic and stamp externalAccountId in a
   * client context. Both the standalone Question Bank panel
   * (`question-bank-screen`) and the in-lesson creation modal
   * (`lesson-question-creation`) pass true.
   */
  scopeToActiveContext: boolean;
}

export interface QuestionDraftState {
  block: QuestionBlock;
  isPublic: boolean;
  errors: string[];
  /** Merge a partial update from an editor into the draft block. */
  setField: (updates: Record<string, unknown>) => void;
  setIsPublic: (next: boolean) => void;
  /** Replace the displayed errors (e.g. push server-side failures). */
  setErrors: (next: string[]) => void;
  /** Re-seed the draft (reopen for create, or open a different question for edit). */
  reset: (block: QuestionBlock, isPublic: boolean) => void;
  /**
   * Builds the wire draft from the current block and validates it client-side.
   * On failure sets `errors` and returns null; on success clears errors and
   * returns the parsed draft for the caller to submit.
   */
  validate: () => QuestionDraft | null;
}

export function useQuestionDraft({
  initialBlock,
  initialIsPublic,
  scopeToActiveContext,
}: UseQuestionDraftOptions): QuestionDraftState {
  const { clientContext } = useClientContext();
  // isPublic is exclusively a Standard-bank concept (NA-403): for a
  // scope-aware caller in a client context, it's never seeded, shown, or
  // submitted — regardless of what was passed in (e.g. a Duplicate/Edit seed
  // carrying the source question's own isPublic value, which only applies in
  // Standard context). A caller that hasn't opted into scoping always
  // resolves to Standard here, unaffected by the active context.
  const scopeAccountId = scopeToActiveContext ? toScopeAccountId(clientContext?.id) : null;
  const [block, setBlock] = useState<QuestionBlock>(initialBlock);
  const [isPublic, setIsPublic] = useState<boolean>(scopeAccountId === null ? initialIsPublic : false);
  const [errors, setErrors] = useState<string[]>([]);

  const setField = useCallback((updates: Record<string, unknown>) => {
    setBlock((prev) => ({ ...prev, ...updates } as QuestionBlock));
  }, []);

  const reset = useCallback((nextBlock: QuestionBlock, nextIsPublic: boolean) => {
    setBlock(nextBlock);
    setIsPublic(scopeAccountId === null ? nextIsPublic : false);
    setErrors([]);
  }, [scopeAccountId]);

  const validate = useCallback((): QuestionDraft | null => {
    // Defense in depth: force isPublic false in the submitted draft whenever
    // scoped, even though the checkbox is hidden and state is already false —
    // the server enforces this independently too (question-bank-storage).
    const effectiveIsPublic = scopeAccountId === null ? isPublic : false;
    const draft = blockToQuestionDraft(block, effectiveIsPublic, scopeAccountId);
    const parsed = questionDraftSchema.safeParse(draft);
    if (!parsed.success) {
      setErrors([...new Set(parsed.error.issues.map((issue) => issue.message))]);
      return null;
    }
    setErrors([]);
    return parsed.data;
  }, [block, isPublic, scopeAccountId]);

  return { block, isPublic, errors, setField, setIsPublic, setErrors, reset, validate };
}

export interface QuestionFormProps {
  block: QuestionBlock;
  onFieldChange: (updates: Record<string, unknown>) => void;
  isPublic: boolean;
  onIsPublicChange: (next: boolean) => void;
  errors: string[];
  autoFocus?: boolean;
  /** NA-403/NA-406: matches the `scopeToActiveContext` option passed to `useQuestionDraft` — see there for details. */
  scopeToActiveContext: boolean;
  /**
   * NA-198: true when editing a question that's in use in one or more
   * lessons — disables every affordance that would shrink the option set
   * (Single Choice remove buttons, Categorization category/item remove
   * buttons, Likert's "Number of options" decreasing) without blocking
   * additions or label edits.
   */
  disableOptionRemoval?: boolean;
  /**
   * NA-198 follow-up: ids of options/categories/items that are actually
   * persisted (i.e. already saved and depended on by the referencing
   * lesson(s)) — only these are locked when `disableOptionRemoval` is true.
   * Anything the author adds locally in this draft (no server id yet) stays
   * removable even while the lock is active.
   */
  lockedOptionIds?: ReadonlySet<string>;
  lockedCategoryIds?: ReadonlySet<string>;
  lockedItemIds?: ReadonlySet<string>;
  /** Likert has no per-row remove control — the persisted point count is the floor decreasing can't cross. */
  lockedMinOptionCount?: number;
}

export function QuestionForm({
  block,
  onFieldChange,
  isPublic,
  onIsPublicChange,
  errors,
  autoFocus = false,
  scopeToActiveContext,
  disableOptionRemoval = false,
  lockedOptionIds,
  lockedCategoryIds,
  lockedItemIds,
  lockedMinOptionCount,
}: QuestionFormProps): React.ReactElement {
  const { tokens } = useTheme();
  const { clientContext } = useClientContext();
  // isPublic/"Add to public question bank" is exclusively a Standard-bank
  // concept (NA-403/NA-406) — the checkbox is omitted entirely in a client
  // context, not merely unchecked, for every scope-aware caller (the
  // standalone Question Bank panel and the in-lesson creation modal).
  const isStandardContext = !scopeToActiveContext || toScopeAccountId(clientContext?.id) === null;

  const styles = useMemo((): Record<string, CSSProperties> => ({
    fieldWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
    },
    fieldLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    textInput: {
      width: '100%',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      outline: 'none',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      cursor: 'pointer',
    },
    checkbox: {
      width: '16px',
      height: '16px',
      cursor: 'pointer',
      accentColor: tokens.brand.primary,
    },
    purposeSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
      marginTop: tokens.spacing.sm,
    },
    purposeLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    // Read-only: same look as the selected Freeform Text Sentiment/Reflection
    // segment (brand fill), but a plain span so it is never clickable.
    purposeValue: {
      alignSelf: 'flex-start',
      minWidth: '110px',
      boxSizing: 'border-box',
      textAlign: 'center',
      padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.sm,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.brand.primary,
      backgroundColor: tokens.brand.primary,
      color: tokens.text.onBrand,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '16px',
      fontWeight: 600,
    },
    errorBox: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.md,
      backgroundColor: tokens.ui.errorBackground,
      border: `1px solid ${tokens.ui.errorBorder}`,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
    },
    errorText: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      color: tokens.ui.errorText,
      margin: 0,
    },
  }), [tokens]);

  // NA-391: read-only — the server derives and stores the real value on save.
  const purpose = deriveQuestionPurpose({
    blockType: block.type,
    gated: block.type === 'single-choice-answer' ? block.gated : undefined,
    hasHealthyResponse: block.type === 'likert-scale' ? (block.healthyResponse ?? 'none') !== 'none' : undefined,
  });

  return (
    <>
      <div style={styles.fieldWrapper}>
        <label style={styles.fieldLabel} htmlFor="question-bank-title">
          Title
        </label>
        <input
          id="question-bank-title"
          type="text"
          value={block.title ?? ''}
          placeholder="Enter title..."
          onChange={(event) => onFieldChange({ title: event.target.value })}
          style={styles.textInput}
        />
      </div>

      {isStandardContext && (
        <label style={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(event) => onIsPublicChange(event.target.checked)}
            style={styles.checkbox}
          />
          Add to public question bank
        </label>
      )}

      {errors.length > 0 && (
        <div style={styles.errorBox} role="alert">
          {errors.map((message) => (
            <p key={message} style={styles.errorText}>{message}</p>
          ))}
        </div>
      )}

      {block.type === 'likert-scale' && (
        <LikertScaleEditor
          block={block}
          onChange={onFieldChange}
          autoFocus={autoFocus}
          variant="questionModal"
          disableOptionRemoval={disableOptionRemoval}
          lockedMinOptionCount={lockedMinOptionCount}
        />
      )}
      {block.type === 'single-choice-answer' && (
        <SingleChoiceAnswerEditor
          block={block}
          onChange={onFieldChange}
          autoFocus={autoFocus}
          variant="questionModal"
          disableOptionRemoval={disableOptionRemoval}
          lockedOptionIds={lockedOptionIds}
        />
      )}
      {block.type === 'workplace-color-spectrum' && (
        <WorkplaceColorSpectrumEditor
          block={block}
          onChange={onFieldChange}
          autoFocus={autoFocus}
          variant="questionModal"
        />
      )}
      {block.type === 'categorization' && (
        <CategorizationEditor
          block={block}
          onChange={onFieldChange}
          autoFocus={autoFocus}
          variant="questionModal"
          disableOptionRemoval={disableOptionRemoval}
          lockedCategoryIds={lockedCategoryIds}
          lockedItemIds={lockedItemIds}
        />
      )}
      {block.type === 'freeform-text' && (
        <FreeformTextEditor
          block={block}
          onChange={onFieldChange}
          autoFocus={autoFocus}
          variant="questionModal"
        />
      )}

      {block.type !== 'freeform-text' && (
        <div style={styles.purposeSection} data-testid="question-purpose">
          <span style={styles.purposeLabel}>Purpose</span>
          <span style={styles.purposeValue}>{purpose}</span>
        </div>
      )}
    </>
  );
}

QuestionForm.displayName = 'QuestionForm';
